
import Animation from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/brevity/animation';
import Timeline from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/brevity/timeline';
import EditorAnimTimeline from './editor-anim-timeline';
import { getTargetComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-component-relate';
import { SequenceTypes, AnimType } from '@tencent/slide-sdk-model';
import { observable, reaction } from 'mobx';
import AnimMediaControlFactory from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-media-control/anim-media-control-factory';
import { getMediaType, setPointerEvents } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import AnimFiltersManager from '../anim-filters/anim-filters-manager';
import { CacheRendering } from '@tencent/alloy-renderer';
import { Action, ATTR_LIST, POINTER } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';

// interface
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import type {
    IEditorAnimPreviewManager, IEditorAnimPreviewManagerDependentServices,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/editor-state-animation/editor-anim-preview-manager.interface';
import type { IPlayManager } from '@src/slide/application/service/play-service/play-manager.interface';
import type { IAnimManager } from '@src/slide/application/service/anim-manager/anim-manager.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type { IRightSideBarManager } from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { ComponentAnimWrapper } from '../../../canvas-render/elements/component-anim-wrapper';
import { colorAnimTypeList } from '@src/js/js-keyframe-animation/style-converter/constants';

export default class EditorAnimPreviewManager implements IEditorAnimPreviewManager {
    public static instance: EditorAnimPreviewManager;
    public static getInstance(): EditorAnimPreviewManager {
        if (!EditorAnimPreviewManager.instance) {
            EditorAnimPreviewManager.instance = new EditorAnimPreviewManager();
        }
        return EditorAnimPreviewManager.instance;
    }

    @observable
    public isAnimPreviewing = false;

    private asyncPreviewAnims;
    private originalStyle;
    private selectionService: ISelectionService;
    private mediaManager: IMediaManager;
    private playManager: IPlayManager;
    private animManager: IAnimManager;
    private rightSideBarManager?: IRightSideBarManager;
    private allowAutoPreview = true;
    private editorAnimTimeline: EditorAnimTimeline;

    public constructor() {
        this.asyncPreviewAnims = null;
        this.originalStyle = {
            alpha: 1,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            skewX: 0,
            skewY: 0,
            x: 0,
            y: 0,
            visible: true,
        };
    }

    public getAutoPreview(): boolean {
        return this.allowAutoPreview;
    }

    public setAutoPreview(value: boolean): void {
        this.allowAutoPreview = value;
    }

    public initWithStore({
        mediaManager,
        playManager,
        animManager,
        rightSideBarManager,
        selectionService,
    }: IEditorAnimPreviewManagerDependentServices): void {
        this.selectionService = selectionService;
        this.mediaManager = mediaManager;
        this.playManager = playManager;
        this.animManager = animManager;
        this.rightSideBarManager = rightSideBarManager;
        AnimMediaControlFactory.setMediaManagerDelegate(mediaManager);
        this.reactAnimEditorShown();
    }
    /**
     * 动画单个预览
     * @param anims
     * @param useBrevityInstance 直接使用 animationInstance 上 brevity 实例 restart 方法(`animationInstance.instance.restart()`),
     * NOTE: 调用实例 animationInstance 上 restart 方法会经历从 EditorAnimTimeline -> BaseTimeline -> handleAnimUpdate 等一系列路径，
     *       导致元素多个动画或复杂动画时动画播放可能丢失部分动画序列
     */
    public previewOneToOneAnims(animModelList: Array<IAnimModel>, useBrevityInstance?: boolean): void {
        const { editSlideComponent } = this.animManager;
        if (!editSlideComponent) return;

        this.editorAnimTimeline = new EditorAnimTimeline({
            animList: animModelList,
            slideComponent: editSlideComponent,
            mediaManager: this.mediaManager,
            options: {
                preview: true,
                editorAnimPreviewManager: this,
            },
            playManager: this.playManager,
        });

        animModelList.forEach((animModel: IAnimModel) => {
            const animModelKey = animModel?.key;
            if (!animModelKey) return;
            const animationInstance = this.editorAnimTimeline?.masterTimeline?.getById(animModelKey);
            if (!animationInstance) return;
            if (useBrevityInstance) {
                animationInstance?.instance?.restart();
            } else {
                animationInstance?.restart();
            }
        });
    }

    /**
     * 动画全部预览
     */
    public previewFromBeginning(): void {
        const mainSeqAnimList = this.getMainSeqAnimList();
        if (!mainSeqAnimList.length) return;
        this.previewWithAnims(mainSeqAnimList);
    }

    /**
     * 取消所有正在运行的动画，并将其恢复至初始状态
     */
    public cancelPreviewingAnims(): void {
        this.isAnimPreviewing = false;
        const animList = this.getAnimModelList();
        if (!animList.length) return;
        // 每次取消运行动画时，需保证slideComponent为当前页
        const canvasApplication = this.animManager.editSlideComponent?.options.getApplication?.();
        if (canvasApplication) {
            canvasApplication.autoRender = true;
        }
        // 清空所有动画，并将主队列动画恢复为初始样式
        const { globalTimeline } = Timeline;
        if (globalTimeline.isActive()) {
            // 清空所有动画
            globalTimeline.clear();
            // 将动画的target恢复到初始样式
            animList.forEach((animModel: IAnimModel) => {
                if (animModel.props.presetClass === presetClassTypes.MEDIA) {
                    this.stopMedia(animModel);
                } else {
                    this.setOriginalStyleForPreview(animModel);
                }
            });
        }
    }

    public updateCurrentSlideElementRefs(): void {
        // TODO 待处理
        if (this.asyncPreviewAnims) {
            this.previewOneToOneAnims(this.asyncPreviewAnims);
        }
    }

    public asyncPreviewWithAnims(anims: Array<IAnimModel>): void {
        // TODO 待处理
        this.asyncPreviewAnims = anims;
    }

    /**
     * 设置预览态动画元素的初始样式
     * @param animModel 动画数据
     */
    private setOriginalStyleForPreview(animModel: IAnimModel): void {
        const animTarget = this.getAnimTarget(animModel);
        if (!animTarget) return;
        const animFilterType = AnimFiltersManager.getInstance().getAnimFilterType(animModel.props.animType);
        if (animFilterType) {
            AnimFiltersManager.getInstance().handleAnimFiltersComplete(
                animFilterType,
                animTarget,
                animModel?.props.presetClass as Action,
                animModel.props.presetClass === presetClassTypes.EXIT,
            );
        }
        Animation.set(animTarget, { ...this.originalStyle, pivotX: 0, pivotY: 0 });
        ATTR_LIST.forEach((attr) => {
            Reflect.deleteProperty(animTarget, attr);
        });
        setPointerEvents(animTarget, POINTER.AUTO);
        this.updateCacheStrategy(animTarget);
        this.handleColorAnim(animModel, animTarget);
    }

    /**
     * 获取动画目标元素
     * @param animModel 动画数据
     * @returns 动画目标元素
     */
    private getAnimTarget(animModel: IAnimModel): ComponentAnimWrapper | undefined {
        const { editSlideComponent } = this.animManager;

        if (!animModel?.elementModel || !editSlideComponent) return;
        const animTarget = getTargetComponent(
            animModel?.elementModel?.key,
            editSlideComponent,
        );
        return animTarget;
    }


    /**
     * 处理颜色动画
     * @param animModel 动画数据
     * @param animTarget 动画目标元素
     */
    private handleColorAnim(animModel: IAnimModel, animTarget: ComponentAnimWrapper): void {
        if (!colorAnimTypeList.includes(animModel.props.animType as AnimType)) return;
        animTarget.factoryOptions.anim = { needUpdateFont: true } ;
        animTarget.factoryUpdateComponent();
    }

    /**
     * 更新缓存策略
     * @param animTarget 动画目标元素
     */
    private updateCacheStrategy(animTarget: ComponentAnimWrapper): void {
        // 预览态动画被中断运行时，不会触发animComplete、childTlComplete、masterTlComplete
        // 由于animStart时设置了始终使用缓存的策略，在中断时需要重置为默认配置
        if (animTarget.cache.rendering !== CacheRendering.Always) return;
        animTarget.cache.rendering =  CacheRendering.Auto;
    }

    /**
     * 停止视频
     * @param animModel 动画数据
     * @param e 鼠标事件
     */
    private stopMedia(animModel: IAnimModel): void | undefined {
        const elementModel = animModel?.elementModel as IElementBaseModel;
        if (!elementModel) return;
        const mediaType = getMediaType(elementModel);
        AnimMediaControlFactory.getInstance(RegionType.EDITOR, mediaType)?.completeMediaControl({ elementModel });
    }

    /**
     * 播放总时间轴控制的所有动画
     * @param animModelList
     */
    private previewWithAnims(animModelList: Array<IAnimModel>): void {
        const { editSlideComponent } = this.animManager;
        if (!editSlideComponent) return;

        this.editorAnimTimeline = new EditorAnimTimeline({
            animList: animModelList,
            slideComponent: editSlideComponent,
            mediaManager: this.mediaManager,
            options: {
                preview: true,
                editorAnimPreviewManager: this,
            },
            playManager: this.playManager,
        });
        this.editorAnimTimeline?.masterTimeline?.restart();
    }

    /**
     * 获取当前页所有动画，包括主队列动画和触发器动画
     * @returns Array<AnimationModel>
     */
    private getAnimModelList(): Array<IAnimModel> {
        const list: Array<IAnimModel> = [];
        this.selectionService.currentSlide?.animList?.forEach((animItem) => {
            if (!Array.isArray(animItem.data)) return;
            list.push(...animItem.data);
        });
        return list;
    }

    /**
     * 获取所有可见的主队列动画, 预览时只播放主队列动画，不播放触发器队列动画
     * @returns Array<AnimationModel>
     */
    private getMainSeqAnimList(): Array<IAnimModel> {
        let mainSeqAnimList: Array<IAnimModel> = [];
        const animList = this.selectionService.currentSlide?.animList || [];
        const mainSeq = animList.find(animListItem => animListItem.sequenceTypes === SequenceTypes.MAIN_SEQ);
        if (mainSeq) {
            const visibleShapeIds = this.selectionService.currentSlide?.children
                ?.filter(({ hidden }) => !hidden)
                ?.map(({ id }) => id);
            mainSeqAnimList = mainSeq?.data?.filter(anim => visibleShapeIds?.includes(anim.spid));
        }
        return mainSeqAnimList;
    }


    /**
     * 右侧动画面板打开，监听mousedown，右侧动画面板关闭，移除监听
     */
    private reactAnimEditorShown = (): void => {
        reaction(() => ({
            isAnimationEditorShown: this.rightSideBarManager?.isAnimationEditorShown,
            isPlaying: this.playManager.isPlaying,
        }), ({ isAnimationEditorShown, isPlaying }) => {
            if (isAnimationEditorShown && !isPlaying) {
                document.addEventListener('mousedown', this.handleMouseDown);
            } else {
                document.removeEventListener('mousedown', this.handleMouseDown);
            }
        });
    };

    /**
     * 编辑态时中止正在运行的动画；演示态下不做处理,直接return
     */
    private handleMouseDown = (): void | undefined => {
        // 有鼠标点击事件，则退出预览态
        this.cancelPreviewingAnims();
    };
}
